import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { AgentRunDetail, AgentRunList } from "@/types/maxwell-runs";

const runsKey = ["maxwell", "runs"] as const;

const PAGE_SIZE = 20;

/**
 * The caller's autonomy runs, newest first, cursor-paged — backs the Activity
 * section. A run that's still advancing (`pending` / `running`) keeps the list
 * polling so its status updates without a manual refresh.
 */
export function useMaxwellRuns() {
  return useInfiniteQuery({
    queryKey: runsKey,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) params.set("before", pageParam);
      return api.get<AgentRunList>(`/api/maxwell/runs?${params.toString()}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const anyActive = pages.some((p) =>
        p.items.some((r) => r.status === "pending" || r.status === "running"),
      );
      return anyActive ? 4000 : false;
    },
  });
}

/** One run with its step timeline — the Activity detail view. */
export function useMaxwellRunDetail(runId: string | null) {
  return useQuery({
    queryKey: [...runsKey, "detail", runId] as const,
    enabled: runId !== null,
    queryFn: () => api.get<AgentRunDetail>(`/api/maxwell/runs/${runId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.run.status;
      return status === "pending" || status === "running" ? 4000 : false;
    },
  });
}

/** Cancel a run — terminal, no further steps drive. */
export function useCancelRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/maxwell/runs/${id}/cancel`, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: runsKey }),
  });
}

/** Delete a run and its steps permanently. */
export function useDeleteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/maxwell/runs/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: runsKey }),
  });
}
